import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from './admin-api.ts';
import { Spinner, PrimaryButton } from './ui.tsx';
import type { CustomQuestion } from './types.ts';

export function CustomQuestionsView({ engagementId }: { engagementId: string }) {
  const queryClient = useQueryClient();
  const dataQ = useQuery({
    queryKey: ['custom-questions', engagementId],
    queryFn: () => adminApi.getCustomQuestions(engagementId),
  });

  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  if (dataQ.isLoading) return <Spinner />;
  if (dataQ.isError) return <p className="text-sm text-red-600">Failed to load custom questions.</p>;

  const { questions, dimensions, customDimensions } = dataQ.data!;

  // Merge standard + custom dimensions for the dropdown
  const allDimensions = [
    ...dimensions.map((d) => ({ id: d.id, slug: d.slug, canonical_name: d.canonical_name, isCustomDim: false })),
    ...customDimensions.map((d) => ({ id: `custom:${d.id}`, slug: `custom-${d.id}`, canonical_name: `★ ${d.name}`, isCustomDim: true })),
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink">Custom Questions</h2>
          <p className="text-sm text-ink-soft">
            Add or edit questions specific to this engagement. These appear alongside the standard 61 questions.
          </p>
        </div>
        <div className="flex gap-2">
          <AddDimensionButton engagementId={engagementId} onDone={() => queryClient.invalidateQueries({ queryKey: ['custom-questions', engagementId] })} />
          <PrimaryButton onClick={() => setShowAdd(true)}>+ Add Question</PrimaryButton>
        </div>
      </div>

      {showAdd && (
        <AddQuestionForm
          dimensions={allDimensions}
          engagementId={engagementId}
          onDone={() => {
            setShowAdd(false);
            queryClient.invalidateQueries({ queryKey: ['custom-questions', engagementId] });
            queryClient.invalidateQueries({ queryKey: ['admin-analysis', engagementId] });
          }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {questions.length === 0 && !showAdd && (
        <p className="py-8 text-center text-sm text-ink-soft">No custom questions yet. Click "+ Add Question" to create one.</p>
      )}

      <ul className="flex flex-col gap-3">
        {questions.map((q) => (
          <li key={q.id} className="rounded-xl border border-black/5 bg-white p-4">
            {editId === q.id ? (
              <EditQuestionForm
                question={q}
                dimensions={allDimensions}
                engagementId={engagementId}
                onDone={() => {
                  setEditId(null);
                  queryClient.invalidateQueries({ queryKey: ['custom-questions', engagementId] });
                  queryClient.invalidateQueries({ queryKey: ['admin-analysis', engagementId] });
                }}
                onCancel={() => setEditId(null)}
              />
            ) : (
              <QuestionCard
                question={q}
                dimensions={allDimensions}
                onEdit={() => setEditId(q.id)}
                onDelete={() => {
                  adminApi.deleteCustomQuestion(engagementId, q.id).then(() => {
                    queryClient.invalidateQueries({ queryKey: ['custom-questions', engagementId] });
                    queryClient.invalidateQueries({ queryKey: ['admin-analysis', engagementId] });
                  });
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuestionCard({
  question,
  dimensions,
  onEdit,
  onDelete,
}: {
  question: CustomQuestion;
  dimensions: { id: string; slug: string; canonical_name: string }[];
  onEdit: () => void;
  onDelete: () => void;
}) {
  const dim = dimensions.find((d) => d.id === question.dimension_id || d.id === `custom:${question.custom_dimension_id}`);
  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <span className="text-xs font-medium text-ink-soft">{dim?.canonical_name ?? 'Unknown'}</span>
          <p className="mt-0.5 text-sm font-medium text-ink">{question.prompt_text}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {[question.anchor_1, question.anchor_2, question.anchor_3, question.anchor_4, question.anchor_5].map((a, i) => (
              <span key={i} className="rounded bg-black/5 px-2 py-0.5 text-[11px] text-ink-soft">{i + 1}: {a}</span>
            ))}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={onEdit} className="text-xs font-medium text-[var(--accent)] hover:underline">Edit</button>
          <button onClick={onDelete} className="text-xs font-medium text-red-600 hover:underline">Delete</button>
        </div>
      </div>
    </div>
  );
}

function AddQuestionForm({
  dimensions,
  engagementId,
  onDone,
  onCancel,
}: {
  dimensions: { id: string; slug: string; canonical_name: string }[];
  engagementId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [dimId, setDimId] = useState(dimensions[0]?.id ?? '');
  const [prompt, setPrompt] = useState('');
  const [anchors, setAnchors] = useState(['Initial / Ad hoc', 'Repeatable / Developing', 'Defined / Functional', 'Managed / Integrated', 'Optimized / Industry Best']);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!prompt.trim()) return;
    setSaving(true);
    const isCustomDim = dimId.startsWith('custom:');
    await adminApi.addCustomQuestion(engagementId, {
      ...(isCustomDim ? { customDimensionId: dimId.replace('custom:', '') } : { dimensionId: dimId }),
      promptText: prompt.trim(),
      anchor1: anchors[0], anchor2: anchors[1], anchor3: anchors[2], anchor4: anchors[3], anchor5: anchors[4],
    });
    setSaving(false);
    onDone();
  };

  return <QuestionForm dimId={dimId} setDimId={setDimId} prompt={prompt} setPrompt={setPrompt} anchors={anchors} setAnchors={setAnchors} dimensions={dimensions} saving={saving} onSave={save} onCancel={onCancel} saveLabel="Add Question" />;
}

function EditQuestionForm({
  question,
  dimensions,
  engagementId,
  onDone,
  onCancel,
}: {
  question: CustomQuestion;
  dimensions: { id: string; slug: string; canonical_name: string }[];
  engagementId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [dimId, setDimId] = useState(question.custom_dimension_id ? `custom:${question.custom_dimension_id}` : (question.dimension_id ?? ''));
  const [prompt, setPrompt] = useState(question.prompt_text);
  const [anchors, setAnchors] = useState([question.anchor_1, question.anchor_2, question.anchor_3, question.anchor_4, question.anchor_5]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!prompt.trim()) return;
    setSaving(true);
    const isCustomDim = dimId.startsWith('custom:');
    await adminApi.updateCustomQuestion(engagementId, question.id, {
      ...(isCustomDim ? { customDimensionId: dimId.replace('custom:', ''), dimensionId: null } : { dimensionId: dimId, customDimensionId: null }),
      promptText: prompt.trim(),
      anchor1: anchors[0], anchor2: anchors[1], anchor3: anchors[2], anchor4: anchors[3], anchor5: anchors[4],
    });
    setSaving(false);
    onDone();
  };

  return <QuestionForm dimId={dimId} setDimId={setDimId} prompt={prompt} setPrompt={setPrompt} anchors={anchors} setAnchors={setAnchors} dimensions={dimensions} saving={saving} onSave={save} onCancel={onCancel} saveLabel="Save Changes" />;
}

function QuestionForm({
  dimId, setDimId, prompt, setPrompt, anchors, setAnchors, dimensions, saving, onSave, onCancel, saveLabel,
}: {
  dimId: string; setDimId: (v: string) => void;
  prompt: string; setPrompt: (v: string) => void;
  anchors: string[]; setAnchors: (v: string[]) => void;
  dimensions: { id: string; slug: string; canonical_name: string }[];
  saving: boolean; onSave: () => void; onCancel: () => void; saveLabel: string;
}) {
  const field = 'w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent-soft)]';

  return (
    <div className="rounded-xl border border-[var(--accent)] bg-[var(--accent-soft)] p-4 space-y-3">
      <div>
        <label className="block text-xs font-medium text-ink-soft mb-1">Dimension</label>
        <select value={dimId} onChange={(e) => setDimId(e.target.value)} className={field}>
          {dimensions.map((d) => <option key={d.id} value={d.id}>{d.canonical_name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-ink-soft mb-1">Question Text</label>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} className={`${field} min-h-[60px] resize-y`} placeholder="Enter question text..." />
      </div>
      <div>
        <label className="block text-xs font-medium text-ink-soft mb-1">Answer Options (Level 1–5)</label>
        <div className="space-y-1.5">
          {anchors.map((a, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs font-bold text-ink-soft w-4">{i + 1}</span>
              <input className={field} value={a} onChange={(e) => { const next = [...anchors]; next[i] = e.target.value; setAnchors(next); }} />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-soft hover:bg-black/5">Cancel</button>
        <PrimaryButton onClick={onSave} disabled={saving || !prompt.trim()}>{saving ? 'Saving…' : saveLabel}</PrimaryButton>
      </div>
    </div>
  );
}


function AddDimensionButton({ engagementId, onDone }: { engagementId: string; onDone: () => void }) {
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await adminApi.addCustomDimension(engagementId, name.trim());
    setSaving(false);
    setName('');
    setShow(false);
    onDone();
  };

  if (!show) {
    return (
      <button
        onClick={() => setShow(true)}
        className="rounded-lg border border-black/10 px-3 py-1.5 text-sm font-medium text-ink-soft hover:bg-black/5"
      >
        + New Dimension
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Dimension name..."
        className="rounded-lg border border-black/10 px-3 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
        onKeyDown={(e) => e.key === 'Enter' && save()}
        autoFocus
      />
      <PrimaryButton onClick={save} disabled={saving || !name.trim()}>
        {saving ? '...' : 'Add'}
      </PrimaryButton>
      <button onClick={() => { setShow(false); setName(''); }} className="text-xs text-ink-soft hover:text-ink">Cancel</button>
    </div>
  );
}
